// Mock data shaped to match production tool returns from src/agent.ts.
// Ported from design_handoff_vgcbuddy_chat_ui/shared/data.js with two
// normalizations:
//   1. `tera` (design) → `teraType` (backend) on every PokemonSpec.
//   2. `evs_text` display strings replaced by structured `evs: StatBlock`;
//      formatEvs() renders the display string at the boundary.
//
// Drop this whole module when Block 11b wires Hono SSE to real agent output.

import type {
  CalcResult,
  Claim,
  EvSpread,
  FirstMoves,
  LeadPrediction,
  MetaLeads,
  PokemonSpec,
  RegulationRef,
  ReplicaCode,
  SessionRef,
  SourceId,
  SpeciesId,
  SpeedTierTable,
  StatBlock,
} from '../types.ts';

// ---------- canonical Pokémon-type colours ----------
export const TYPE_COLORS: Record<string, string> = {
  normal: '#A8A878',
  fire: '#F08030',
  water: '#6890F0',
  electric: '#F8D030',
  grass: '#78C850',
  ice: '#98D8D8',
  fighting: '#C03028',
  poison: '#A040A0',
  ground: '#E0C068',
  flying: '#A890F0',
  psychic: '#F85888',
  bug: '#A8B820',
  rock: '#B8A038',
  ghost: '#705898',
  dragon: '#7038F8',
  dark: '#705848',
  steel: '#B8B8D0',
  fairy: '#EE99AC',
};

// ---------- citation source registry ----------
export const CITATION_SOURCES: Record<SourceId, { label: string; hue: string; textHue: string }> = {
  pikalytics: { label: 'Pikalytics', hue: '#d4a04a', textHue: '#f1c97a' },
  limitless: { label: 'Limitless', hue: '#7d6cd0', textHue: '#b3a8ff' },
  'pokemon-showdown': { label: 'PS! Replays', hue: '#4a86d4', textHue: '#7fb1f0' },
  'note-com': { label: 'note.com', hue: '#3da888', textHue: '#5fc9a8' },
  'liberty-note': { label: 'Liberty Note', hue: '#c4576e', textHue: '#e88198' },
  'cybertron-vgc': { label: 'Cybertron VGC', hue: '#5a6fd0', textHue: '#8a9bff' },
  'wolfey-vgc': { label: 'WolfeyVGC', hue: '#d07a3d', textHue: '#f0a06d' },
  'yoshi37-vgc': { label: '13Yoshi37', hue: '#7ab07a', textHue: '#9bc99b' },
  'joeux9-vgc': { label: 'JoeUX9', hue: '#4ab09b', textHue: '#7fc9b8' },
  // JP creators — get distinct hues + the JA pill at render time. We want
  // the user to SEE that they're getting Japanese source content because
  // English-only competitors don't have it.
  'viera-jp': { label: 'Viera (JP)', hue: '#d44a86', textHue: '#f07fb1' },
  'kinugawa-jp': { label: 'Kinugawa (JP)', hue: '#a04ad4', textHue: '#c97ff0' },
  // Internal: damage-calculator verified. Greenish so visually distinct from
  // external-source pills. Always cite this when a Verdict row's number came
  // from a calc_damage / find_min_ev_to_survive tool result in the same turn.
  'damage-calc': { label: 'calc', hue: '#88b07a', textHue: '#a8c994' },
};

// ---------- nature → stat effect map ----------
// Replaces verbose nature names with ▲stat / ▼stat glyphs.
export const NATURE_EFFECTS: Record<string, { up: keyof StatBlock; down: keyof StatBlock } | null> =
  {
    Adamant: { up: 'atk', down: 'spa' },
    Modest: { up: 'spa', down: 'atk' },
    Jolly: { up: 'spe', down: 'spa' },
    Timid: { up: 'spe', down: 'atk' },
    Careful: { up: 'spd', down: 'spa' },
    Calm: { up: 'spd', down: 'atk' },
    Bold: { up: 'def', down: 'atk' },
    Impish: { up: 'def', down: 'spa' },
    Sassy: { up: 'spd', down: 'spe' },
    Brave: { up: 'atk', down: 'spe' },
    Quiet: { up: 'spa', down: 'spe' },
    Relaxed: { up: 'def', down: 'spe' },
    Lonely: { up: 'atk', down: 'def' },
    Naughty: { up: 'atk', down: 'spd' },
    Naive: { up: 'spe', down: 'spd' },
    Hasty: { up: 'spe', down: 'def' },
    Mild: { up: 'spa', down: 'def' },
    Rash: { up: 'spa', down: 'spd' },
    Gentle: { up: 'spd', down: 'def' },
    Lax: { up: 'def', down: 'spd' },
    Hardy: null,
    Docile: null,
    Bashful: null,
    Quirky: null,
    Serious: null,
  };

// ---------- species registry ----------
export type MonRef = { name: string; slug: string; types: string[] };
export const MONS: Record<SpeciesId, MonRef> = {
  sneasler: { name: 'Sneasler', slug: 'sneasler', types: ['fighting', 'poison'] },
  garchomp: { name: 'Garchomp', slug: 'garchomp', types: ['dragon', 'ground'] },
  kingambit: { name: 'Kingambit', slug: 'kingambit', types: ['dark', 'steel'] },
  basculegion: { name: 'Basculegion', slug: 'basculegion', types: ['water', 'ghost'] },
  'charizard-mega-y': {
    name: 'Charizard-Mega-Y',
    slug: 'charizard-megay',
    types: ['fire', 'flying'],
  },
  'floette-eternal': { name: 'Floette-Eternal', slug: 'floette-eternal', types: ['fairy'] },
  incineroar: { name: 'Incineroar', slug: 'incineroar', types: ['fire', 'dark'] },
  whimsicott: { name: 'Whimsicott', slug: 'whimsicott', types: ['grass', 'fairy'] },
  aerodactyl: { name: 'Aerodactyl', slug: 'aerodactyl', types: ['rock', 'flying'] },
  sinistcha: { name: 'Sinistcha', slug: 'sinistcha', types: ['grass', 'ghost'] },
  'urshifu-rapid-strike': {
    name: 'Urshifu-Rapid-Strike',
    slug: 'urshifu-rapidstrike',
    types: ['fighting', 'water'],
  },
  pelipper: { name: 'Pelipper', slug: 'pelipper', types: ['water', 'flying'] },
  rillaboom: { name: 'Rillaboom', slug: 'rillaboom', types: ['grass'] },
  amoonguss: { name: 'Amoonguss', slug: 'amoonguss', types: ['grass', 'poison'] },
  ogerpon: { name: 'Ogerpon', slug: 'ogerpon', types: ['grass'] },
  ironhands: { name: 'Iron Hands', slug: 'ironhands', types: ['fighting', 'electric'] },
};

// ---------- sprite fallback chain ----------
// Showdown's slug conventions are inconsistent across forms — see the design
// handoff for the 5-level fallback. For now, gen5 only; expand if a sprite 404s.
export function spriteUrl(slug: string): string {
  return `https://play.pokemonshowdown.com/sprites/gen5/${slug}.png`;
}

// ---------- EV formatting (replaces design's evs_text strings) ----------
const STAT_LABEL: Record<keyof StatBlock, string> = {
  hp: 'HP',
  atk: 'Atk',
  def: 'Def',
  spa: 'SpA',
  spd: 'SpD',
  spe: 'Spe',
};

export function formatEvs(evs: StatBlock): string {
  const order: (keyof StatBlock)[] = ['hp', 'atk', 'def', 'spa', 'spd', 'spe'];
  return order
    .filter((k) => evs[k] > 0)
    .map((k) => `${evs[k]} ${STAT_LABEL[k]}`)
    .join(' / ');
}

// ---------- active regulation ----------
export const ACTIVE_REGULATION: RegulationRef = {
  id: 'M-A',
  label: 'Reg M-A',
  note: 'Pokémon Champions',
};

// ---------- user's team ----------
export const MY_TEAM: PokemonSpec[] = [
  {
    species: 'incineroar',
    item: 'Safety Goggles',
    ability: 'Intimidate',
    teraType: 'ghost',
    nature: 'Careful',
    evs: { hp: 252, atk: 0, def: 4, spa: 0, spd: 252, spe: 0 },
    moves: ['Fake Out', 'Knock Off', 'Parting Shot', 'Will-O-Wisp'],
  },
  {
    species: 'whimsicott',
    item: 'Covert Cloak',
    ability: 'Prankster',
    teraType: 'steel',
    nature: 'Timid',
    evs: { hp: 252, atk: 0, def: 0, spa: 4, spd: 0, spe: 252 },
    moves: ['Tailwind', 'Moonblast', 'Encore', 'Light Screen'],
  },
  {
    species: 'garchomp',
    item: 'Life Orb',
    ability: 'Rough Skin',
    teraType: 'steel',
    nature: 'Jolly',
    evs: { hp: 4, atk: 252, def: 0, spa: 0, spd: 0, spe: 252 },
    moves: ['Earthquake', 'Dragon Claw', 'Stone Edge', 'Protect'],
  },
  {
    species: 'sinistcha',
    item: 'Leftovers',
    ability: 'Hospitality',
    teraType: 'water',
    nature: 'Bold',
    evs: { hp: 252, atk: 0, def: 252, spa: 0, spd: 4, spe: 0 },
    moves: ['Matcha Gotcha', 'Trick Room', 'Calm Mind', 'Protect'],
  },
  {
    species: 'aerodactyl',
    item: 'Focus Sash',
    ability: 'Tailwind',
    teraType: 'flying',
    nature: 'Jolly',
    evs: { hp: 4, atk: 252, def: 0, spa: 0, spd: 0, spe: 252 },
    moves: ['Tailwind', 'Rock Slide', 'Wide Guard', 'Taunt'],
  },
  {
    species: 'kingambit',
    item: 'Assault Vest',
    ability: 'Defiant',
    teraType: 'fairy',
    nature: 'Adamant',
    evs: { hp: 100, atk: 252, def: 0, spa: 0, spd: 156, spe: 0 },
    moves: ['Kowtow Cleave', 'Sucker Punch', 'Iron Head', 'Low Kick'],
  },
];

// ---------- opponent's team preview (species only, like Showdown reveals) ----------
export const OPP_TEAM: SpeciesId[] = [
  'charizard-mega-y',
  'floette-eternal',
  'sneasler',
  'aerodactyl',
  'garchomp',
  'kingambit',
];

// ---------- lead prediction ----------
export const LEAD_PREDICTION: LeadPrediction = {
  predictions: [
    {
      lead_pair: ['charizard-mega-y', 'floette-eternal'],
      count: 12,
      fraction_of_matchups: 0.5,
      match_kind: 'exact',
    },
    {
      lead_pair: ['sneasler', 'aerodactyl'],
      count: 6,
      fraction_of_matchups: 0.25,
      match_kind: 'fuzzy',
    },
    {
      lead_pair: ['charizard-mega-y', 'sneasler'],
      count: 4,
      fraction_of_matchups: 0.17,
      match_kind: 'fuzzy',
    },
  ],
  matchups_used: 24,
  exact_matchups: 17,
};

// ---------- first moves per species ----------
export const FIRST_MOVES: Record<SpeciesId, FirstMoves> = {
  'charizard-mega-y': {
    species: 'charizard-mega-y',
    total_appearances: 28,
    results: [
      { move: 'Heat Wave', count: 19, fraction: 0.679 },
      { move: 'Protect', count: 6, fraction: 0.214 },
      { move: 'Solar Beam', count: 2, fraction: 0.071 },
      { move: '(no_move)', count: 1, fraction: 0.036 },
    ],
  },
  'floette-eternal': {
    species: 'floette-eternal',
    total_appearances: 22,
    results: [
      { move: 'Moonblast', count: 12, fraction: 0.545 },
      { move: 'Dazzling Gleam', count: 7, fraction: 0.318 },
      { move: 'Protect', count: 3, fraction: 0.136 },
    ],
  },
  sneasler: {
    species: 'sneasler',
    total_appearances: 31,
    results: [
      { move: 'Close Combat', count: 21, fraction: 0.677 },
      { move: 'Dire Claw', count: 6, fraction: 0.194 },
      { move: 'Fake Out', count: 3, fraction: 0.097 },
      { move: '(no_move)', count: 1, fraction: 0.032 },
    ],
  },
};

// ---------- meta leads snapshot ----------
export const META_LEADS: MetaLeads = {
  replays_considered: 1847,
  results: [
    { species: 'sneasler', count: 462, fraction: 0.25 },
    { species: 'charizard-mega-y', count: 387, fraction: 0.209 },
    { species: 'aerodactyl', count: 296, fraction: 0.16 },
    { species: 'whimsicott', count: 248, fraction: 0.134 },
    { species: 'incineroar', count: 213, fraction: 0.115 },
  ],
};

// ---------- speed tiers ----------
export const SPEED_TIERS: SpeedTierTable = {
  my_speed: 156,
  my_species: 'garchomp',
  rows: [
    {
      species: 'sneasler',
      common_spread: '252 Spe / Jolly',
      final_speed: 156,
      outpaces_me: false,
      notes: 'speed tie',
    },
    {
      species: 'aerodactyl',
      common_spread: '252 Spe / Jolly',
      final_speed: 167,
      outpaces_me: true,
    },
    {
      species: 'whimsicott',
      common_spread: '252 Spe / Timid',
      final_speed: 144,
      outpaces_me: false,
    },
    {
      species: 'charizard-mega-y',
      common_spread: '252 Spe / Timid',
      final_speed: 167,
      outpaces_me: true,
    },
    {
      species: 'floette-eternal',
      common_spread: '252 Spe / Timid',
      final_speed: 156,
      outpaces_me: false,
    },
    {
      species: 'garchomp',
      common_spread: '252 Spe / Jolly',
      final_speed: 156,
      outpaces_me: false,
      notes: 'this is you',
    },
    { species: 'kingambit', common_spread: '0 Spe / Adamant', final_speed: 81, outpaces_me: false },
    { species: 'incineroar', common_spread: '0 Spe / Sassy', final_speed: 67, outpaces_me: false },
    {
      species: 'sinistcha',
      common_spread: '0 Spe / Bold',
      final_speed: 51,
      outpaces_me: false,
      notes: 'TR mode',
    },
  ],
};

// ---------- damage calcs ----------
function fillRolls(min: number, max: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < 16; i++) {
    out.push(Math.round(min + ((max - min) * i) / 15));
  }
  return out;
}

export const DAMAGE_CALCS: CalcResult[] = [
  {
    attacker: MY_TEAM[2], // garchomp
    defender: { ...MY_TEAM[0], species: 'charizard-mega-y' }, // shape stub; only fields used in card matter
    move: 'Stone Edge',
    damage: { min: 268, max: 316, minPct: 168.5, maxPct: 198.7 },
    koChance: { text: 'guaranteed OHKO', chance: 1.0 },
    rolls: fillRolls(268, 316),
    isOhko: true,
    desc: '252 Atk Life Orb Garchomp Stone Edge vs. 0 HP / 0 Def Charizard-Mega-Y: 268-316 (168.5 - 198.7%) -- guaranteed OHKO',
    variant: 'super',
  },
];

// ---------- ev spreads ----------
export const EV_SPREADS: EvSpread[] = [
  {
    defender: {
      species: 'incineroar',
      item: 'Safety Goggles',
      ability: 'Intimidate',
      nature: 'Careful',
    },
    attacker: { species: 'charizard-mega-y', move: 'Heat Wave', evs_text: '252 SpA / Timid / Sun' },
    stat: 'spd',
    achievable: true,
    minEvs: 156,
    finalStat: 137,
    survivalRate: 1.0,
    damage: { min: 42, max: 51, minPct: 21.0, maxPct: 25.5 },
    note: 'survives a 4HKO comfortably',
  },
];

// ---------- claims ----------
export const CLAIMS: Claim[] = [
  {
    claim_type: 'matchup_advice',
    confidence: 0.86,
    quote:
      "If you see Charizard-Mega-Y with Floette and you're not running a steel-tera priority answer, you're behind on turn one — Heat Wave just clicks.",
    channel_name: 'CybertronVGC',
    video_title: 'Reg M-A Power Rankings — April 2026',
    source_url: 'https://youtu.be/dQw4w9WgXcQ',
    source_timestamp_sec: 624,
    pokemon_referenced: ['charizard-mega-y', 'floette-eternal'],
  },
];

// ---------- replica codes ----------
export const REPLICA_CODES: ReplicaCode[] = [
  {
    code: 'F4LK-J92R-XW',
    confidence: 0.94,
    source_url: 'https://twitter.com/wolfeyvgc/status/...',
    context: 'team posted by Wolfe Glick after Top 4 finish at OCIC',
    verified: true,
  },
  {
    code: 'B7HQ-MN3P-VT',
    confidence: 0.81,
    source_url: 'https://note.com/jvgc_user/n/abcde',
    context: 'JP team report — Charizard / Floette balance',
    verified: false,
  },
];

// ---------- session history ----------
export const SESSION_HISTORY: SessionRef[] = [
  {
    id: 's12',
    title: 'Trick Room build · Sinistcha + Basculegion',
    relative: '2 hours ago',
    regulation: 'M-A',
    turns: 23,
    kind: 'build',
    pinned: true,
  },
  {
    id: 's11',
    title: 'Counter to sun balance',
    relative: 'yesterday',
    regulation: 'M-A',
    turns: 14,
    kind: 'matchup',
  },
  {
    id: 's10',
    title: 'EV solve · Garchomp survives Knock Off',
    relative: 'Sunday',
    regulation: 'M-A',
    turns: 8,
    kind: 'ev',
  },
];

// ---------- suggested prompts (empty state) ----------
export const SUGGESTED_PROMPTS: string[] = [
  'Predict opponent leads against my team',
  'Build a team around Glimmora and Whimsicott',
  'Optimize my Sneasler EVs against the meta',
  'Top threats in Reg M-A right now',
  'Find tournament teams running Mega Drampa',
  'Speed tier audit my Garchomp at +1',
];
